import React, { useState, useEffect, useCallback } from "react";
import eventService from "../../service/event.service";
import { DataTable } from "mantine-datatable";
import CustomPagination from "../../components/Layouts/CustomPagination";

const AllUsers = () => {
    // Pagination and Users state
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [csvLoading, setCsvLoading] = useState(false);
    const [page, setPage] = useState(0); // 0-indexed for CustomPagination
    const PAGE_SIZES = [10, 20, 50];
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    const fetchUsers = useCallback(async () => {
        try {
            setUsersLoading(true);
            // Send page + 1 if API is 1-indexed
            const response = await eventService.getAllUsersWithTeamCount(page + 1, pageSize);
            const result = response?.data;
            setUsers(result?.data || []);
            setTotalRecords(result?.pagination?.totalItems || 0);
        } catch (error) {
            console.error("Error fetching users:", error);
            setUsers([]);
            setTotalRecords(0);
        } finally {
            setUsersLoading(false);
        }
    }, [page, pageSize]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handlePaginationChange = (type, newPage) => {
        const totalPages = Math.ceil(totalRecords / pageSize);
        if (type === "Next") {
            if (page < totalPages - 1) setPage(page + 1);
        } else if (type === "Previous") {
            if (page > 0) setPage(page - 1);
        } else if (type === "First") {
            setPage(0);
        } else if (type === "Last") {
            setPage(totalPages - 1);
        } else {
            setPage(newPage);
        }
    };

    const handleDataPerPageChange = (newPageSize) => {
        setPageSize(newPageSize);
        setPage(0); // Reset to first page when changing page size
    };

    const downloadCSV = async () => {
        try {
            setCsvLoading(true);
            const response = await eventService.getAllUsersForCSV();
            const allUsers = response?.data?.data || [];
            
            if (allUsers.length === 0) {
                alert('No users data available');
                return;
            }
            
            // Create CSV content
            const headers = ['User ID', 'Name', 'Email', 'Team Count'];
            const csvContent = [
                headers.join(','),
                ...allUsers.map(user => [
                    user.userId || '',
                    `"${user.name || '-'}"`, // Show "-" for missing names
                    `"${user.email || ''}"`,
                    user.teamCount || 0
                ].join(','))
            ].join('\n');
            
            // Create blob and download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `all_users_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert('Failed to download CSV. Please try again.');
        } finally {
            setCsvLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">All Users</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        View all users with their team counts
                    </p>
                </div>
                <button
                    onClick={downloadCSV}
                    disabled={csvLoading}
                    className="btn btn-outline-primary"
                >
                    {csvLoading ? (
                        <>
                            <span className="animate-spin mr-2">⏳</span>
                            Downloading...
                        </>
                    ) : (
                        <>
                            <span className="mr-2">📥</span>
                            Download CSV
                        </>
                    )}
                </button>
            </div>
            
            <div className="panel">
                <div className="flex items-center justify-between mb-5">
                    <h5 className="font-semibold text-lg dark:text-white-light">
                        Users List ({totalRecords} total users)
                    </h5>
                </div>

                <div className="datatables">
                    <DataTable
                        highlightOnHover
                        className="whitespace-nowrap table-hover"
                        records={users}
                        columns={[
                            { accessor: "userId", title: "User ID", width: 100 },
                            { 
                                accessor: "name", 
                                title: "Name",
                                render: ({ name }) => name || "-"
                            },
                            { accessor: "email", title: "Email" },
                            { 
                                accessor: "teamCount", 
                                title: "Team Count", 
                                textAlignment: "center",
                                width: 120,
                                render: ({ teamCount }) => (
                                    <span className={`badge ${teamCount > 0 ? 'badge-success' : 'badge-secondary'}`}>
                                        {teamCount}
                                    </span>
                                )
                            },
                        ]}
                        minHeight={200}
                        loading={usersLoading}
                        noRecordsText="No users found"
                    />
                    
                    {users.length > 0 && (
                        <div className="mt-5">
                            <CustomPagination
                                total={totalRecords}
                                page={page}
                                handleChange={handlePaginationChange}
                                dataPerPage={pageSize}
                                handleDataPerPage={handleDataPerPageChange}
                                PAGE_SIZES={PAGE_SIZES}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AllUsers;
